/**
 * Dify APIウォームアップ処理関連のユーティリティ
 */
import { api } from "~/api";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { warmupPrompt } from "~/Commons";

/**
 * Dify API ウォームアップ処理
 * 初回起動時に一度だけダミーリクエストを送信してAPI接続を確立する
 * ウォームアップで作成された会話は自動削除される
 * @param sessionId セッションID
 */
export const warmupDifyConnection = async (sessionId: string): Promise<void> => {
  const warmupKey = "difyWarmupCompleted";

  // 既にこのセッションで実行済みならスキップ
  if (sessionStorage.getItem(warmupKey)) {
    return;
  }

  // 顧客IDを取得
  const customerId = await getCurrentCustomerId();

  // ウォームアップリクエストを送信
  await sendWarmupRequest(sessionId, customerId);

  // ウォームアップで作成された会話IDを取得して削除
  const conversationId = await getWarmupConversationId(sessionId);

  if (conversationId) {
    // 会話IDが取得できた場合は削除
    await deleteWarmupConversation(conversationId, customerId);
  }

  // 完了をセッションストレージに記録して会話履歴統計のポーリングをトリガー
  sessionStorage.setItem(warmupKey, "true");
};

/**
 * ウォームアップリクエストを送信する
 * @param sessionId セッションID
 * @param customerId カスタマーID
 */
const sendWarmupRequest = async (sessionId: string, customerId: string): Promise<void> => {
  await Promise.race([
    api.dify.reception.mutate({
      customerId: customerId,
      message: "ウォームアップ用のリクエストです。「WARMED」とだけ返信してください。",
      resetFlag: true,
      characterIdentification: "",
      userLanguage: "ja-JP",
      talkingMode: 0,
      updatePrompt: warmupPrompt,
      sessionId: sessionId,
      user: "warmer",
    }),
    new Promise<void>((_, reject) => setTimeout(() => reject(new Error("Warmup timeout")), 30000)),
  ]);
};

/**
 * ウォームアップで作成された会話IDを取得する
 * @param sessionId セッションID
 * @returns 会話ID
 */
const getWarmupConversationId = async (sessionId: string): Promise<string | null> => {
  const maxRetries = 10; // 10回リトライ
  const retryInterval = 5000; // 5秒
  let conversationId: string | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      conversationId = await api.dify.getConversationId.query({
        sessionId: sessionId,
      });

      // 会話IDが取得できた場合はループを抜ける
      if (conversationId) {
        break;
      }

      // 取得できない場合は待機してリトライ
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, retryInterval));
      }
    } catch {
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, retryInterval));
      }
    }
  }
  return conversationId;
};

/**
 * ウォームアップで作成された会話を削除する
 * @param conversationId 会話ID
 * @param customerId カスタマーID
 */
const deleteWarmupConversation = async (
  conversationId: string,
  customerId: string
): Promise<void> => {
  await api.dify.deleteConversation.mutate({
    conversationId,
    customerId,
    user: "warmer", // ウォームアップ専用ユーザ識別子
  });
  console.info("DifyAPI Warming Completed.");
};
